/**
 * Approval Queue Page (Spoor 2, Verlof MVP — Story 3.1/3.2, UX V4/V5)
 *
 * Goedkeuringslijst met beslis-context: alle (team- of, voor HR/admin, alle)
 * aanvragen, In afwachting bovenaan, met per rij de aanvrager, het
 * verloftype, de periode/uren, het resterend saldo + saldo ná goedkeuring,
 * en de teamoverlap (AC 1/2). Story 3.2 bedraadt de Goedkeuren (één klik) /
 * Afwijzen (verplichte reden, `RejectDialog`) acties op dezelfde pagina.
 *
 * AC 4 (Story 3.1): het oog-icoon opent een lichte "mini-doorkijk" (een
 * popover met de lijst overlappende collega's uit dezelfde
 * teamoverlap-query) in plaats van een volledige kalender-embed — Story 4.2
 * (Epic 4, calendarService) kan dit later vervangen.
 *
 * Zelf-aanvraag (UX V5): een leidinggevende ziet geen besluit-acties op de
 * eigen rij — dit is een UI-laag-verdediging bovenop de bindende
 * server-side `SELF_APPROVAL_FORBIDDEN`-check (AD-4); de server mag hier
 * nooit op vertrouwen en wordt hier ook niet op vertrouwd (de check blijft
 * server-side afgedwongen ongeacht wat de UI toont).
 *
 * Story 2.6 (FR-10, UX D1): urenwijzigingen staan in DEZELFDE lijst, niet in
 * een apart tabblad — een tweede lijst is een tweede lijst die je kunt
 * vergeten. Ze zijn herkenbaar aan de violette chip `Urenwijziging` en de
 * oud→nieuw-regel; de teller bovenaan splitst uit zodra beide soorten
 * openstaan. AD-24: op zo'n rij hangt de zelf-check aan de INDIENER van de
 * correctie, niet aan de medewerker wiens verlof het betreft.
 */

import { useState } from 'react';
import { Layout } from '../../components/Layout';
import { useApprovalQueue } from './hooks/useApprovalQueue';
import { useSelectableEmployees } from './hooks/useSelectableEmployees';
import { useVerlofsoortIngangsdatum } from './hooks/useVerlofsoortIngangsdatum';
import { LeaveRequestSheet } from './LeaveRequestSheet';
import { useDecideLeaveRequest } from './hooks/useDecideLeaveRequest';
import { useDecideLeaveCorrection } from './hooks/useDecideLeaveCorrection';
import { useDecideHrStapCorrectie } from './hooks/useDecideHrStapCorrectie';
import { useDecideHrStap } from './hooks/useDecideHrStap';
import { useRetrySync } from './hooks/useRetrySync';
import { useRetractApproval } from './hooks/useRetractApproval';
import { useUpdateLeaveEndDate } from './hooks/useUpdateLeaveEndDate';
import { useCorrigeerVerlofsoort } from './hooks/useCorrigeerVerlofsoort';
import {
  SOORT_ACTIE,
  SOORT_BEVESTIGING_UITLEG,
  SOORT_BEVESTIGING_KNOP,
} from './verlofsoortCorrectieTeksten';
import {
  VERLOFSOORT_VRAAG,
  VERLOFSOORT_KEUZE_TEKSTEN,
  VERLOFSOORT_FOUT_GEEN_KEUZE,
} from './verlofsoortKeuzeTeksten';
import { StatusBadge } from './StatusBadge';
import { SyncStatusChip } from './SyncStatusChip';
import { RejectDialog } from './RejectDialog';
import { TeamOnbevestigdeUrenBlok } from './TeamOnbevestigdeUrenBlok';
import { formatNlUren } from './urenFormat';
import {
  WachtOpHrChip,
  HrStapRedenChip,
  MaandruimteWaarschuwing,
  UrenwijzigingBovenMaandruimteRegel,
  UrenwijzigingIndienerRegelOpDeLijst,
  GeenSoortRegel,
  LeidinggevendeDoorstuurRegel,
  goedkeurKnopTekst,
  hrGoedkeurKnopTekst,
  hrBesluitBevestiging,
  hrTellerDelen,
  boektNaGoedkeuring,
  ligtBijVan,
  REDEN_UITLEG_HR,
} from './HrStapStatus';
import {
  AANHOUDEN_MAG,
  EINDDATUM_WORDT_DEFINITIEF,
  MARKERING_EINDDATUM_VOORLOPIG,
  REGEL_VOOR_HR,
  verhuisSignaalZin,
} from './bijzonderVerlofTeksten';
import { useAuth } from '../auth';
import { ApiRequestError } from '../../lib/api';
import { VERLOFSOORTEN, bijzondereGebeurtenisNaam } from '@functieloonhuis/shared';
import type { ApprovalQueueItem, RequestStatus, Verlofsoort } from '@functieloonhuis/shared';

/** UX-DR11: violet, dezelfde kleurfamilie als verlof — nooit rood. */
const URENWIJZIGING_INK = '#7C6FCF';
const URENWIJZIGING_BG = '#F1EFFA';

const STATUS_FILTER_OPTIONS: { value: RequestStatus | 'alle'; label: string }[] = [
  { value: 'alle', label: 'Alle statussen' },
  { value: 'in_afwachting', label: 'In afwachting' },
  { value: 'goedgekeurd', label: 'Goedgekeurd' },
  { value: 'afgewezen', label: 'Afgewezen' },
  { value: 'ingetrokken', label: 'Ingetrokken' },
];

function OverlapIndicator({ item }: { item: ApprovalQueueItem }) {
  const [isOpen, setIsOpen] = useState(false);

  if (item.teamOverlapCount === 0) {
    return <span className="text-xs text-gray-400">Geen teamoverlap</span>;
  }

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className="text-xs text-[#54949E] hover:underline inline-flex items-center gap-1"
        aria-expanded={isOpen}
      >
        <span aria-hidden="true">👁</span>
        {item.teamOverlapCount} teamleden al afwezig in deze periode
      </button>
      {isOpen && (
        <div
          data-testid="overlap-popover"
          className="absolute z-10 mt-1 w-64 bg-white border border-[#E2E8F0] rounded-md shadow-lg p-3 text-xs text-[#1E293B]"
        >
          <div className="font-medium mb-1">Wie is afwezig in deze periode</div>
          <ul className="space-y-1">
            {item.overlappingColleagues.map((c) => (
              <li key={c.employeeId}>
                {c.name} ({c.startDate} — {c.endDate})
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function BalanceContext({ item }: { item: ApprovalQueueItem }) {
  if (item.remainingBalance === null) {
    return <span className="text-xs text-gray-400">Saldo niet beschikbaar</span>;
  }

  // Past de aanvraag niet in de opgebouwde stand maar wél in de jaarruimte,
  // dan is dat geen probleem — het is februari-rekenwerk, geen tekort. Zonder
  // die regel leest "saldo ná goedkeuring: -40 uur" als een reden om af te
  // wijzen terwijl de medewerker het jaar gewoon uitbouwt.
  const tekortNaGoedkeuring = item.balanceAfterApproval !== null && item.balanceAfterApproval < 0;
  const pastInJaarruimte = item.yearRemaining !== null && item.hours <= item.yearRemaining;

  return (
    <div className="text-xs text-gray-600">
      <div>Opgebouwd saldo: {item.remainingBalance} uur</div>
      <div>Saldo ná goedkeuring: {item.balanceAfterApproval} uur</div>
      {item.yearRemaining !== null && (
        <div>Nog te plannen dit jaar: {item.yearRemaining} uur</div>
      )}
      {tekortNaGoedkeuring && pastInJaarruimte && (
        <div className="text-[#2F5A7A] mt-0.5">
          Past binnen de jaarruimte — de opbouw loopt dit jaar nog door.
        </div>
      )}
    </div>
  );
}

/**
 * Story 2.6 (AC-1, UX D1): de chip die een urenwijziging herkenbaar maakt.
 * De betekenis zit in de TEKST (AC-12) — de kleur is versiering, geen drager.
 * Inline style, niet Tailwind: zo is de kleureis ook in een test te bewijzen.
 */
function UrenwijzigingChip() {
  return (
    <span
      data-testid="urenwijziging-chip"
      className="inline-block px-2 py-0.5 rounded-full text-xs font-medium"
      style={{ backgroundColor: URENWIJZIGING_BG, color: URENWIJZIGING_INK }}
    >
      Urenwijziging
    </span>
  );
}

/** "09:00–11:00  2 uur" — of alleen "2 uur" als dit verlof geen tijden had (AD-11). */
function urenWeergave(uren: number, start: string | null, eind: string | null): string {
  const urenTekst = `${formatNlUren(uren)} uur`;
  return start && eind ? `${start}–${eind}  ${urenTekst}` : urenTekst;
}

/**
 * Story 2.6 (AC-1/AC-11/AC-12): het correctieblok op de kaart — oud naast
 * nieuw, het verschil eronder, de toelichting van de indiener, en een
 * eventuele saldo-overschrijding.
 */
function CorrectieBlok({ item }: { item: ApprovalQueueItem }) {
  const correctie = item.correctie;
  if (!correctie) return null;

  const minder = correctie.verschil < 0;

  return (
    <div data-testid="correctie-blok" className="mt-3 text-sm text-[#1E293B]">
      <p data-testid="correctie-oud-nieuw">
        <span>{urenWeergave(correctie.oldHours, correctie.oldStartTime, correctie.oldEndTime)}</span>
        <span aria-hidden="true" className="mx-2 text-gray-400">
          →
        </span>
        <span className="font-medium">
          {urenWeergave(correctie.newHours, correctie.newStartTime, correctie.newEndTime)}
        </span>
      </p>

      {correctie.verschil !== 0 && (
        // AC-12: naast kleur altijd het woord "minder" of "meer" én een
        // pijl-teken — kleurenblind lezen blijft mogelijk.
        <p
          data-testid="correctie-verschil"
          className={`mt-1 font-medium ${minder ? 'text-[#5C7A00]' : 'text-[#2F5A7A]'}`}
        >
          {minder
            ? `▼ ${formatNlUren(Math.abs(correctie.verschil))} uur minder`
            : `▲ ${formatNlUren(correctie.verschil)} uur meer`}
        </p>
      )}

      <p data-testid="correctie-toelichting" className="mt-2 text-gray-600 italic">
        {`“${correctie.reason}”`}
        {correctie.submittedByName ? (
          <span className="not-italic text-gray-500">{` — ${correctie.submittedByName}`}</span>
        ) : null}
      </p>

      {correctie.balanceWarning && (
        // AC-11: zichtbaar bij het besluit, maar nooit een blokkade —
        // managerdiscretie.
        <p
          data-testid="correctie-saldo-waarschuwing"
          className="mt-2 text-xs text-[#2F5A7A]"
        >
          {`Let op: dit gaat ${formatNlUren(correctie.balanceWarning.hours)} uur over het verlofsaldo heen.`}
        </p>
      )}
    </div>
  );
}

/**
 * Story 7.6, AC-5 (§10.5, bindend onder KE-7) — de vastgestelde zin bij een
 * geblokkeerde aanvraag. Letterlijk, en zónder de tweede zin over wat HR nog
 * wél zou kunnen: hier kan HR er níet omheen.
 *
 * De datum komt voluit zonder jaartal, zoals de zin in §10.5 hem toont. Zonder
 * bekende uitdienstdatum blijft de zin weg — een halve zin verzinnen mag niet.
 */
const MAANDEN_VOLUIT = [
  'januari',
  'februari',
  'maart',
  'april',
  'mei',
  'juni',
  'juli',
  'augustus',
  'september',
  'oktober',
  'november',
  'december',
];

function uitDienstZin(naam: string, uitDienstDatum: string | null): string | null {
  if (!uitDienstDatum) return null;
  const [, maand, dag] = uitDienstDatum.split('-');
  if (!maand || !dag) return null;
  const maandNaam = MAANDEN_VOLUIT[Number(maand) - 1];
  if (!maandNaam) return null;
  return `${naam} is uit dienst per ${Number(dag)} ${maandNaam}. Deze aanvraag kan niet meer worden goedgekeurd.`;
}

/**
 * Story 7.7 (§10.5, besluit V29) en story 7.8 (§10.5, besluiten V37/V38) — de
 * vastgestelde schermteksten, letterlijk. KE-7: overgenomen zoals ze in het
 * handboek staan; alleen getallen en datums worden ingevuld.
 */
const TERUGNEMEN_ACTIE = 'Goedkeuring terugnemen';
const TERUGNEMEN_UITLEG =
  'De aanvraag komt terug in je eigen lijst. HR beoordeelt hem dan niet meer.';
const TERUGNEMEN_KNOP = 'Terugnemen';
const TERUGNEMEN_RACE =
  'HR heeft deze aanvraag intussen al beoordeeld. Terugnemen kan niet meer.';
const EINDDATUM_ACTIE = 'Einddatum aanpassen';
const EINDDATUM_BEVESTIGING =
  'De uren worden opnieuw berekend uit het rooster. Daarna kun je de periode niet meer aanpassen.';

/**
 * Story 10.3, AC-1 / Story 10.4, AC-4 — wat er bij een bijzonder-verlofaanvraag
 * in de lijstregel zelf komt te staan.
 *
 * DRIE KRINGEN, EN ZE VERSCHILLEN (§8.2, §8.3):
 *
 * - de GEBEURTENIS is voor de medewerker, zijn leidinggevende en HR — hij
 *   staat er in woorden en nooit als sleutel (KE-3);
 * - de AFWIJKING, het verhuis-signaal en de twee regels van §10.4 horen bij de
 *   HR-STAP en staan niet in de lijst van de leidinggevende;
 * - de MARKERING *Einddatum nog niet definitief* is er weer voor alle drie.
 *
 * DE ROL IS DE STAP NIET (code review 2026-08-17). Hier stond `zietDeHrKant`,
 * afgeleid uit `user.role` — waarmee HR bij een aanvraag die nog bij de
 * leidinggevende ligt de regel *"Pas de einddatum aan voordat je goedkeurt"*
 * las, terwijl de knop er niet eens staat: `isEinddatumAanpasbaar` kijkt naar
 * `ligtBijVan(item)`. AC-4 zegt bovendien met zoveel woorden *alleen bij de
 * HR-stap*. Overal in dit bestand is dat `ligtBijVan(item) === 'hr'`; hier nu
 * ook, met de rol er nog naast als leesrecht.
 *
 * GEEN LOSSE REGEL MET HET HANDBOEKGETAL (besluit van de opdrachtgever,
 * 2026-08-17). Er stonden twee zelfbedachte regels — *Handboek: N werkdagen* en
 * *Aangevraagd: N werkdagen* — die §10.3 niet kent, en bij een afwijking las HR
 * hetzelfde getal twee keer in twee eenheidswoorden. Wat overblijft is de
 * vastgestelde afwijkingszin, en die verschijnt alleen bij een afwijking.
 *
 * Geen waarschuwingsvorm, in geen van de regels: de tabel stelt voor en
 * begrenst niet (besluit V12, AD-38). Gewone tekst in de documentstroom, geen
 * `title`-attribuut en geen betekenis die alleen uit kleur volgt (KE-3).
 */
function BijzonderVerlofContext({
  item,
  zietDeHrKant,
  aanpasvensterOpen,
}: {
  item: ApprovalQueueItem;
  zietDeHrKant: boolean;
  /** Staat het venster van story 7.8 open op déze rij? Dan toont dát de afwijking. */
  aanpasvensterOpen: boolean;
}) {
  if (item.soort === 'urenwijziging') return null;

  // De STAP, niet de rol — plus het leesrecht, want een leidinggevende die
  // toevallig bij de HR-stap staat is nog steeds geen HR.
  const bijDeHrStap = zietDeHrKant && ligtBijVan(item) === 'hr';

  const gebeurtenis = bijzondereGebeurtenisNaam(item.bijzondereGebeurtenis ?? null);
  const heeftAfwijking = bijDeHrStap && handboekAfwijking(item) !== null;
  const afwijking = heeftAfwijking && !aanpasvensterOpen ? handboekAfwijking(item) : null;
  const verhuisdag = bijDeHrStap ? (item.eerdereVerhuisdag ?? null) : null;
  const voorlopig = item.einddatumVoorlopig === true;

  if (gebeurtenis === null && afwijking === null && verhuisdag === null && !voorlopig) {
    return null;
  }

  return (
    <div
      data-testid="bijzonder-verlof-context"
      className="mt-2 text-xs text-gray-600 border-t border-[#E2E8F0] pt-2 space-y-1"
    >
      {gebeurtenis !== null && <p>{`Gebeurtenis: ${gebeurtenis}`}</p>}
      {afwijking !== null && <p data-testid="handboek-afwijking-lijst">{afwijking}</p>}
      {verhuisdag !== null && <p data-testid="verhuis-signaal">{verhuisSignaalZin(verhuisdag)}</p>}
      {voorlopig && (
        <>
          <p data-testid="einddatum-voorlopig-markering" className="font-medium text-[#2F5A7A]">
            {MARKERING_EINDDATUM_VOORLOPIG}
          </p>
          {/* §10.4: de twee regels voor HR komen BOVENOP die van §10.5 — de
              handeling heet nog steeds *Einddatum aanpassen* en de bevestiging
              is nog steeds die van story 7.8. */}
          {bijDeHrStap && (
            <>
              <p>{REGEL_VOOR_HR}</p>
              <p>{AANHOUDEN_MAG}</p>
            </>
          )}
        </>
      )}
    </div>
  );
}


/**
 * Story 5.5, AC-1/AC-4/AC-6 — valt de soort nog te corrigeren?
 *
 * Drie voorwaarden, en ze staan alle drie ook in de dienst (AD-2): er is nog
 * geen besluit genomen, en de rij heeft de loonadministratie op geen enkele
 * manier al bereikt. Een urenwijziging heeft geen soort en valt hierbuiten.
 *
 * Dat de aanvraag toevallig bij HR of nog bij de leidinggevende ligt maakt
 * niet uit — de voorwaarde is dát er nog niets besloten is (AC-1).
 */
function isSoortAanpasbaar(item: ApprovalQueueItem): boolean {
  // Story 11.1, AC-9: deze uitsluiting BLIJFT staan, en de reden is dat een
  // urenwijziging geen verlofsoort heeft — er valt op deze rij niets te
  // corrigeren. De soort woont op de onderliggende aanvraag, en dáár staat de
  // ingang.
  if (item.soort === 'urenwijziging') return false;
  if (item.requestStatus !== 'in_afwachting') return false;
  return item.syncStatus === 'pending' || item.syncStatus === 'niet_boekend';
}

/**
 * Story 7.7, AC-1 — valt er iets terug te nemen, en door WIE?
 *
 * Er is een goedkeuring gegeven (`decided_at`) en HR heeft nog niets gedaan
 * (`hr_decided_at` leeg). Een urenwijziging heeft zijn eigen goedkeuringsas en
 * valt hierbuiten.
 *
 * Story 8.2, AC-4: de eigenaarstoets staat sinds deze story HIER en niet meer
 * in een schermfilter dat de hele rij weggooide. De server stuurt zo'n rij nu
 * mee — dat is wat de terugname mogelijk houdt — maar de knop blijft van hém
 * alleen: een goedkeuring van een ánder is niet van hem om terug te nemen.
 * HR en beheerders zien de actie onverkort (story 7.7, AC-9): zij konden de
 * aanvraag toch al afwijzen.
 */
function isTerugTeNemen(
  item: ApprovalQueueItem,
  currentUserId: number | undefined,
  zietDeHrKant: boolean
): boolean {
  // Story 11.1, AC-9: deze uitsluiting BLIJFT staan. Een urenwijziging heeft
  // sinds deze story wél een eigen goedkeuringsas, maar er is geen terugname-
  // pad voor een correctie — `retractLeaveApproval` werkt uitsluitend op de
  // aanvraagrij. Een knop tonen die de server niet kent is erger dan geen knop.
  if (item.soort === 'urenwijziging') return false;
  if (item.hrDecidedAt != null) return false;
  if (item.decidedAt == null) return false;
  if (zietDeHrKant) return true;
  return currentUserId !== undefined && item.decidedBy === currentUserId;
}

/**
 * Story 7.8, AC-1/AC-2/AC-5 — mag de einddatum hier nog aangepast worden?
 *
 * Alleen wanneer de aanvraag op HR wacht en HR nog niet besliste. Kort verlof
 * met een van-tot-tijd valt af: dat speelt zich binnen één dag af, dus er is
 * geen einddatum te verschuiven.
 */
function isEinddatumAanpasbaar(item: ApprovalQueueItem): boolean {
  if (item.soort === 'urenwijziging') return false;
  if (item.hrDecidedAt != null) return false;
  if (item.startTime != null || item.endTime != null) return false;
  return ligtBijVan(item) === 'hr';
}

/**
 * Story 7.8 (§10.3, besluit V12/AD-38) — het aangevraagde aantal dagen naast
 * wat het handboek voorstelt, of `null` wanneer er niets naast te leggen valt.
 *
 * DIT IS GEEN WAARSCHUWING. De tabel stelt voor en begrenst niet; een
 * afwijking is geen fout en krijgt dus geen foutvorm en geen foutkleur.
 */
function handboekAfwijking(item: ApprovalQueueItem): string | null {
  const aangevraagd = item.aangevraagdeDagen;
  const handboek = item.handboekDagen;
  if (aangevraagd == null || handboek == null) return null;
  // ── Besluit van de opdrachtgever, 2026-08-16: ALLEEN BIJ MÉÉR ──
  //
  // De vergelijking was `!==` en toonde de zin dus ook bij MINDER dan het
  // maximum. Bij een gebeurtenis met een venster (*"maximaal 5 werkdagen"*) is
  // minder vragen juist de normale gang van zaken; in beide richtingen tonen
  // levert bij bijna elke aanvraag een melding op, en een melding die bijna
  // altijd verschijnt wordt niet meer gelezen — dan is het signaal weg op het
  // moment dat het er wél toe doet. Bij een gebeurtenis met een vast aantal
  // (*"1 dag"*) valt elk verschil naar boven hier vanzelf onder.
  if (aangevraagd <= handboek) return null;
  return `Aangevraagd: ${aangevraagd} dagen. Het handboek noemt hier ${handboek} werkdagen.`;
}

interface DecisionActionsProps {
  item: ApprovalQueueItem;
  currentEmployeeId?: number;
  currentUserId?: number;
  onApprove: (item: ApprovalQueueItem) => void;
  onReject: (item: ApprovalQueueItem) => void;
  onRetract: (item: ApprovalQueueItem) => void;
  onEditEndDate: (item: ApprovalQueueItem) => void;
  onEditSoort: (item: ApprovalQueueItem) => void;
  magEinddatumAanpassen: boolean;
  magSoortAanpassen: boolean;
  /** Story 8.2, AC-4: HR en beheerders zien de terugname-actie onverkort. */
  zietDeHrKant: boolean;
  isPending: boolean;
}

function DecisionActions({
  item,
  currentEmployeeId,
  currentUserId,
  onApprove,
  onReject,
  onRetract,
  onEditEndDate,
  onEditSoort,
  magEinddatumAanpassen,
  magSoortAanpassen,
  zietDeHrKant,
  isPending,
}: DecisionActionsProps) {
  if (item.requestStatus !== 'in_afwachting') return null;

  // Story 11.1, AC-9: deze tak BLIJFT staan, en hij zet de HR-stap niet uit —
  // hij kiest wélke persoon de zelf-beoordeelpoort vergelijkt.
  if (item.soort === 'urenwijziging') {
    // AD-24: bij een urenwijziging vergelijkt de poort de INDIENER van de
    // correctie (een users.id), niet de medewerker wiens verlof het betreft.
    // Dezelfde regel als de server afdwingt; dit is alleen de UI-laag.
    if (currentUserId !== undefined && item.correctie?.submittedBy === currentUserId) return null;
    // Code review 2026-08-18: ligt de wijziging bij HR, dan heeft de
    // leidinggevende er geen besluit meer op. De server laat zo'n rij alleen
    // nog in zijn lijst staan wanneer híj de eerste goedkeurder was, zodat hij
    // hem kan blijven VOLGEN (story 7.7) — en `isTerugTeNemen` sluit een
    // urenwijziging uit, dus er valt ook niets terug te nemen. Bleven
    // *Goedkeuren* en *Afwijzen* staan, dan zijn dat twee knoppen die allebei
    // 403 opleveren. Wat overblijft is de STAND: de chip *Wacht op HR* staat
    // boven op de kaart en verdwijnt hier niet mee.
    if (!zietDeHrKant && ligtBijVan(item) === 'hr') return null;
  } else if (currentEmployeeId !== undefined && item.employeeId === currentEmployeeId) {
    // UX V5: geen besluit-acties op de eigen rij (UI-laag-verdediging bovenop
    // de bindende server-side SELF_APPROVAL_FORBIDDEN-check, AD-4).
    return null;
  }

  // Story 11.1, AC-9: de knoptekst van HR hangt aan de STAND, en sinds deze
  // story kan een urenwijziging ook bij HR liggen — de uitsluiting op de soort
  // is hier dus weg.
  const ligtBijHr = ligtBijVan(item) === 'hr';
  // Story 7.6, AC-6/AC-6b: bij een geblokkeerde aanvraag verdwijnt *Goedkeuren*
  // — een knop die een handeling suggereert die niet kan is het vormprincipe
  // van FR-27 op zijn kop. *Afwijzen* blijft er wél staan: dat is de enige weg
  // om de aanvraag van een vertrokken medewerker nog op te ruimen (besluit
  // B-V5). De blokkade komt van de server; het scherm bepaalt hem niet zelf.
  const geblokkeerd = item.uitDienstBlokkade != null;

  return (
    <div className="flex gap-2">
      {!geblokkeerd && (
        <button
          type="button"
          onClick={() => onApprove(item)}
          disabled={isPending}
          className="px-3 py-1.5 rounded-md bg-[#B7D945] text-[#1E293B] text-xs font-medium disabled:opacity-40"
        >
          {/* Twee besluiten, twee knopteksten, allebei zeggen ze wat er echt
              gebeurt. Story 7.2, AC-7: goedkeuren door de leidinggevende STUURT
              DOOR. Story 7.3, AC-5: het besluit van HR is de laatste
              goedkeuring, dus daar hangt de boeking aan — of juist niet, bij een
              soort die de loonadministratie nooit bereikt (§10.8, FR-23). */}
          {ligtBijHr ? hrGoedkeurKnopTekst(boektNaGoedkeuring(item)) : goedkeurKnopTekst(item)}
        </button>
      )}
      <button
        type="button"
        onClick={() => onReject(item)}
        disabled={isPending}
        className="px-3 py-1.5 rounded-md border border-[#D64545]/40 text-[#D64545] text-xs font-medium disabled:opacity-40"
      >
        Afwijzen
      </button>
      {/* Story 7.7, AC-1/AC-9: zolang HR nog niet keek kan de goedkeuring
          terug. Ook HR ziet deze actie — dezelfde lijst, dezelfde teksten
          (bevinding 4); HR kon de aanvraag toch al afwijzen. */}
      {isTerugTeNemen(item, currentUserId, zietDeHrKant) && (
        <button
          type="button"
          onClick={() => onRetract(item)}
          disabled={isPending}
          className="px-3 py-1.5 rounded-md border border-[#54949E]/40 text-[#54949E] text-xs font-medium disabled:opacity-40"
        >
          {TERUGNEMEN_ACTIE}
        </button>
      )}
      {/* Story 7.8, AC-1/AC-3: alleen HR of admin, en alleen zolang HR nog
          moet beslissen. Aanpassen en beslissen zijn twee handelingen. */}
      {magEinddatumAanpassen && isEinddatumAanpasbaar(item) && (
        <button
          type="button"
          onClick={() => onEditEndDate(item)}
          disabled={isPending}
          className="px-3 py-1.5 rounded-md border border-[#2F5A7A]/40 text-[#2F5A7A] text-xs font-medium disabled:opacity-40"
        >
          {EINDDATUM_ACTIE}
        </button>
      )}
      {/* Story 5.5, AC-5: alleen HR of een beheerder — niet de medewerker en
          niet de leidinggevende, ook niet die de aanvraag zelf goedkeurde. De
          rolpoort staat óók in de dienst; dit is de tweede laag. */}
      {magSoortAanpassen && isSoortAanpasbaar(item) && (
        <button
          type="button"
          onClick={() => onEditSoort(item)}
          disabled={isPending}
          className="px-3 py-1.5 rounded-md border border-[#2F5A7A]/40 text-[#2F5A7A] text-xs font-medium disabled:opacity-40"
        >
          {SOORT_ACTIE}
        </button>
      )}
    </div>
  );
}

/**
 * Story 5.5, AC-1/AC-11 — de bevestiging bij een soortcorrectie (besluit V42).
 *
 * De uitleg noemt het gevolg en niet het mechanisme; de knop is het ene woord
 * dat V42 vastlegt. Naar het model van `RetractDialog` en `EndDateDialog`, met
 * de keuze uit de gedeelde tabel zodat er nergens een soortnaam wordt
 * overgeschreven (AD-27).
 */
function SoortDialog({
  item,
  onClose,
  onConfirm,
  isPending,
}: {
  item: ApprovalQueueItem | null;
  onClose: () => void;
  onConfirm: (nieuweSoort: Verlofsoort) => void;
  isPending: boolean;
}) {
  const [gekozen, setGekozen] = useState<Verlofsoort | ''>('');

  if (!item) return null;

  const huidige = (item.verlofsoort as Verlofsoort | null) ?? null;
  const nieuweSoort = gekozen || huidige;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div
        className="w-full max-w-md bg-white rounded-lg p-6 shadow-xl"
        data-testid="verlofsoort-dialog"
      >
        <h2 className="text-lg font-bold text-[#1E293B] mb-3">{SOORT_ACTIE}</h2>

        <label htmlFor="nieuwe-verlofsoort" className="block text-sm text-gray-600 mb-1">
          {VERLOFSOORT_VRAAG}
        </label>
        <select
          id="nieuwe-verlofsoort"
          value={nieuweSoort ?? ''}
          onChange={(e) => setGekozen(e.target.value as Verlofsoort)}
          className="w-full border border-[#E2E8F0] rounded-md p-2 text-sm"
        >
          {/* Een EXPLICIETE lege beginoptie (code review 2026-08-17). Een rij
              zonder soort — verlof van vóór de ingangsdatum, en al het verlof
              dat aan dit werk voorafging (FR-19) — geeft dit veld de waarde
              `''`. Zonder een optie die daarbij hoort kiest de browser de
              EERSTE optie om te tonen: het veld leest dan *Medisch bezoek
              onder werktijd* terwijl er niets gekozen is en de knop uit staat.
              De tekst is de vastgestelde zin uit §10.1 en wordt hier niet
              opnieuw bedacht (KE-7). */}
          <option value="" disabled>
            {VERLOFSOORT_FOUT_GEEN_KEUZE}
          </option>
          {(Object.keys(VERLOFSOORTEN) as Verlofsoort[]).map((soort) => (
            <option key={soort} value={soort}>
              {VERLOFSOORT_KEUZE_TEKSTEN[soort].label}
            </option>
          ))}
        </select>

        <p className="mt-3 text-sm text-gray-700">{SOORT_BEVESTIGING_UITLEG}</p>

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            className="px-4 py-2 rounded-md text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={() => nieuweSoort && onConfirm(nieuweSoort)}
            disabled={isPending || !nieuweSoort}
            className="px-4 py-2 rounded-md bg-[#2F5A7A] text-white text-sm font-medium disabled:opacity-40"
          >
            {SOORT_BEVESTIGING_KNOP}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Story 7.7, AC-1/AC-3 — de bevestiging bij een terugname (§10.5, besluit
 * V29).
 *
 * Geen redenveld: §10.5 kent er geen. Een eigen, kleine dialoog naast
 * `RejectDialog` juist daarom — die dialoog bestáát bij de gratie van zijn
 * verplichte reden.
 */
function RetractDialog({
  isOpen,
  onClose,
  onConfirm,
  isPending,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isPending: boolean;
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="w-full max-w-md bg-white rounded-lg p-6 shadow-xl" data-testid="retract-dialog">
        <h2 className="text-lg font-bold text-[#1E293B] mb-3">{TERUGNEMEN_ACTIE}</h2>
        <p className="text-sm text-gray-700">{TERUGNEMEN_UITLEG}</p>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            className="px-4 py-2 rounded-md text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isPending}
            className="px-4 py-2 rounded-md bg-[#54949E] text-white text-sm font-medium disabled:opacity-40"
          >
            {TERUGNEMEN_KNOP}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Story 7.8, AC-12 — het venster waarin HR de einddatum vaststelt.
 *
 * Eén bevestigingszin voor álle soorten (besluit V38): hij noemt de
 * loonadministratie niet, want daar komt calamiteiten- en zorgverlof nooit,
 * terwijl deze handeling voor elke soort geldt die op HR wacht.
 *
 * KE-3: het datumveld draagt een zichtbaar, gekoppeld label, en het
 * herberekende aantal dagen wordt in een `aria-live`-gebied aangekondigd —
 * positie of kleur is nooit de enige drager.
 */
function EndDateDialog({
  item,
  onClose,
  onConfirm,
  isPending,
}: {
  item: ApprovalQueueItem | null;
  onClose: () => void;
  onConfirm: (nieuweEinddatum: string) => void;
  isPending: boolean;
}) {
  const [nieuweEinddatum, setNieuweEinddatum] = useState('');

  if (!item) return null;

  const afwijking = handboekAfwijking(item);
  const gekozen = nieuweEinddatum || item.endDate;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div
        className="w-full max-w-md bg-white rounded-lg p-6 shadow-xl"
        data-testid="einddatum-dialog"
      >
        <h2 className="text-lg font-bold text-[#1E293B] mb-3">{EINDDATUM_ACTIE}</h2>

        <label htmlFor="nieuwe-einddatum" className="block text-sm text-gray-600 mb-1">
          Nieuwe einddatum
        </label>
        <input
          id="nieuwe-einddatum"
          type="date"
          value={gekozen}
          min={item.startDate}
          onChange={(e) => setNieuweEinddatum(e.target.value)}
          className="w-full border border-[#E2E8F0] rounded-md p-2 text-sm"
        />

        <p className="mt-3 text-sm text-gray-700">{EINDDATUM_BEVESTIGING}</p>

        {afwijking && (
          // Besluit V12/AD-38: een voorstel, geen grens — dus in de gewone
          // tekstkleur en zonder `role="alert"`.
          <p data-testid="handboek-afwijking" className="mt-2 text-sm text-gray-600">
            {afwijking}
          </p>
        )}

        <p aria-live="polite" data-testid="einddatum-herberekening" className="mt-2 text-sm text-gray-600">
          {`Nieuwe periode: ${item.startDate} t/m ${gekozen}. De uren worden na het opslaan opnieuw uit het rooster berekend.`}
          {/* Story 10.4, AC-6 (KE-3): draagt de aanvraag de markering, dan
              verdwijnt die met deze handeling — en dat wordt samen met de
              nieuwe periode aangekondigd, niet alleen zichtbaar. Zonder deze
              regel verdwijnt de markering ongemerkt voor wie het scherm niet
              ziet. De woorden zijn door de opdrachtgever vastgesteld op
              2026-08-17 en staan als `EINDDATUM_WORDT_DEFINITIEF` op de ene
              plek waar de teksten van §10 wonen (KE-7). */}
          {item.einddatumVoorlopig === true ? ` ${EINDDATUM_WORDT_DEFINITIEF}` : ''}
        </p>

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            className="px-4 py-2 rounded-md text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={() => onConfirm(gekozen)}
            disabled={isPending}
            className="px-4 py-2 rounded-md bg-[#2F5A7A] text-white text-sm font-medium disabled:opacity-40"
          >
            Periode aanpassen
          </button>
        </div>
      </div>
    </div>
  );
}

export function ApprovalQueuePage() {
  const { data, isLoading, error } = useApprovalQueue();
  const { user } = useAuth();
  const decide = useDecideLeaveRequest();
  const decideCorrection = useDecideLeaveCorrection();
  const decideHrStap = useDecideHrStap();
  const decideHrStapCorrectie = useDecideHrStapCorrectie();
  const retrySync = useRetrySync();
  const retractApproval = useRetractApproval();
  const updateEndDate = useUpdateLeaveEndDate();
  const corrigeerSoort = useCorrigeerVerlofsoort();
  const [statusFilter, setStatusFilter] = useState<RequestStatus | 'alle'>('alle');
  const [rejectTarget, setRejectTarget] = useState<ApprovalQueueItem | null>(null);
  // Story 7.7/7.8: twee handelingen die ieder hun eigen bevestiging hebben.
  const [retractTarget, setRetractTarget] = useState<ApprovalQueueItem | null>(null);
  const [endDateTarget, setEndDateTarget] = useState<ApprovalQueueItem | null>(null);
  // Story 5.5: de soortcorrectie heeft zijn eigen bevestiging, met de teksten
  // die de opdrachtgever op 2026-08-16 heeft vastgesteld (besluit V42).
  const [soortTarget, setSoortTarget] = useState<ApprovalQueueItem | null>(null);
  const [balanceWarning, setBalanceWarning] = useState<{ applicantName: string; hours: number } | null>(null);
  const [decideError, setDecideError] = useState<string | null>(null);
  // Story 7.3, AC-5: de bevestiging ná een HR-goedkeuring — welke van de twee
  // hangt aan de soort, niet aan de knop die erop is gedrukt.
  const [hrBevestiging, setHrBevestiging] = useState<{ regel: string; uitleg: string | null } | null>(
    null
  );
  const [onBehalfFor, setOnBehalfFor] = useState<{ id: number; name: string } | null>(null);
  // Story 5.4 (AC-7): de schakelaar. Zolang hij niet gezet is zwijgt
  // `GeenSoortRegel` en staat er in deze lijst precies wat er vandaag staat.
  const ingangsdatum = useVerlofsoortIngangsdatum().data ?? null;

  // Vóór wie mag deze gebruiker verlof invoeren? De server bepaalt de lijst;
  // is die leeg, dan verschijnt de ingang niet.
  const { data: selectableResp, isLoading: isLoadingSelectable, isError: isSelectableError } = useSelectableEmployees();
  const selectableEmployees = selectableResp?.data ?? [];

  // Story 8.2, AC-1/AC-4: DE SERVER HEEFT AL BEPAALD WELKE RIJEN HIER HOREN.
  // Dit scherm toonde tot deze story zelf welke aanvragen bij HR lagen en
  // welke de leidinggevende nog mocht terugnemen — twee afleidingen die naast
  // de server liepen (KE-11, AD-31). Ze zijn verhuisd: de lijst wordt
  // onverkort getoond zoals hij binnenkomt.
  const items = data?.data ?? [];
  const zietDeHrKant = user?.role === 'hr' || user?.role === 'admin';
  // ... maar meetellen als openstaand doet hij niet: de leidinggevende hoeft
  // er niets meer mee, tenzij hij zich bedenkt (story 7.2, AC-8).
  const openstaand = items.filter(
    (i) => i.requestStatus === 'in_afwachting' && (zietDeHrKant || ligtBijVan(i) !== 'hr')
  );
  // Story 7.3, AC-2: de aanvragen die op HR wachten worden apart geteld, per
  // reden. De rest van de lijst houdt de uitsplitsing van epic 2.
  // Story 11.1, AC-9 (KE-12): een urenwijziging met een HR-stap telt hier MEE.
  // Sloot deze teller hem uit, dan zag HR de correctie wel in de lijst maar
  // niet in het aantal erboven — en dan telt de kop minder op dan wat eronder
  // staat.
  const openstaandBijHr = openstaand.filter((i) => ligtBijVan(i) === 'hr');
  const overigOpenstaand = openstaand.filter((i) => !openstaandBijHr.includes(i));
  const openstaandeAanvragen = overigOpenstaand.filter((i) => i.soort !== 'urenwijziging').length;
  const openstaandeWijzigingen = overigOpenstaand.filter((i) => i.soort === 'urenwijziging').length;

  // AC-2 (§10.5, letterlijk): "4 openstaand — 1 boven de 3 uur, 2 bijzonder
  // verlof, 1 calamiteiten- of zorgverlof". Het tellen per reden staat in
  // `HrStapStatus`, bij de rest van de HR-stap-afleiding.
  const hrDelen = hrTellerDelen(openstaandBijHr);
  // AC-2 (UX-DR14, letterlijk): "7 openstaand — 5 aanvragen, 2 urenwijzigingen".
  // Zonder HR-stap-items blijft die teller exact zoals hij was (KE-6): de
  // uitsplitsing verschijnt alleen als er ook echt beide soorten liggen. Staat
  // er wél een HR-teller, dan hoort de rest er volledig bij te staan — anders
  // telt de kop meer op dan de delen eronder.
  const restDelen =
    hrDelen.length > 0
      ? [
          openstaandeAanvragen > 0
            ? `${openstaandeAanvragen} ${openstaandeAanvragen === 1 ? 'aanvraag' : 'aanvragen'}`
            : null,
          openstaandeWijzigingen > 0
            ? `${openstaandeWijzigingen} ${
                openstaandeWijzigingen === 1 ? 'urenwijziging' : 'urenwijzigingen'
              }`
            : null,
        ].filter((deel): deel is string => deel !== null)
      : openstaandeAanvragen > 0 && openstaandeWijzigingen > 0
        ? [
            `${openstaandeAanvragen} ${openstaandeAanvragen === 1 ? 'aanvraag' : 'aanvragen'}`,
            `${openstaandeWijzigingen} ${
              openstaandeWijzigingen === 1 ? 'urenwijziging' : 'urenwijzigingen'
            }`,
          ]
        : [];
  const alleDelen = [...hrDelen, ...restDelen];
  const tellerTekst =
    alleDelen.length > 0
      ? `${openstaand.length} openstaand — ${alleDelen.join(', ')}`
      : `${openstaand.length} openstaand`;
  // ── Story 11.1, AC-9: een urenwijziging die op HR wacht hoort niet meer in
  // de lijst van de leidinggevende ──
  //
  // De server trekt dezelfde grens en stuurt zo'n rij alleen nog mee wanneer
  // híj de eerste goedkeurder was; dat is precies wat de terugname van story
  // 7.7 mogelijk houdt. Het scherm laat hem daarom staan zolang die
  // uitzondering geldt en verbergt hem anders — de uitzondering staat op de
  // rij zelf (`decidedBy`), niet op de rol.
  const zichtbareItems = zietDeHrKant
    ? items
    : items.filter((i) => {
        if (i.soort !== 'urenwijziging') return true;
        if (ligtBijVan(i) !== 'hr') return true;
        return i.decidedBy === user?.id && i.decidedAt != null && i.hrDecidedAt == null;
      });
  const filteredItems =
    statusFilter === 'alle'
      ? zichtbareItems
      : zichtbareItems.filter((i) => i.requestStatus === statusFilter);
  const isPending =
    decide.isPending ||
    decideCorrection.isPending ||
    decideHrStap.isPending ||
    decideHrStapCorrectie.isPending;

  /**
   * Story 7.3: ligt deze rij bij HR, dan gaat het besluit langs het HR-pad.
   *
   * Story 11.1, AC-7: dat geldt sinds deze story ook voor een urenwijziging —
   * die heeft zijn eigen HR-eindpunt, dat de HR-kolommen van de CORRECTIE
   * schrijft. Welk van de twee paden het wordt staat hieronder; de vraag *ligt
   * dit bij HR?* is voor allebei dezelfde.
   */
  function isHrBesluit(item: ApprovalQueueItem): boolean {
    return ligtBijVan(item) === 'hr';
  }

  async function handleApprove(item: ApprovalQueueItem) {
    setDecideError(null);
    setHrBevestiging(null);
    try {
      // Story 7.3, AC-3/AC-5: een aanvraag die bij HR ligt krijgt het
      // HR-besluit, met de bevestiging die bij de soort hoort. Nooit het
      // gewone besluitpad — dat schrijft de kolommen van de eerste beslisser
      // (AD-29).
      if (isHrBesluit(item) && item.soort === 'urenwijziging') {
        // `item.id` IS het correctie-id (zie `ApprovalQueueItem.id`).
        const res = await decideHrStapCorrectie.mutateAsync({
          id: item.id,
          decision: 'approve',
        });
        if (res.data.balanceWarning) {
          setBalanceWarning({
            applicantName: item.applicantName,
            hours: res.data.balanceWarning.hours,
          });
        }
        return;
      }
      if (isHrBesluit(item)) {
        await decideHrStap.mutateAsync({ id: item.id, decision: 'approve' });
        // Code review 2026-08-17: het RUWE serverveld, niet `boektNaGoedkeuring`
        // — die valt bij een ontbrekend veld terug op *boekt wel*, en dat is
        // precies de mededeling die dan niet gedaan mag worden. Ontbreekt het
        // veld, dan levert `hrBesluitBevestiging` `null` en staat er niets.
        setHrBevestiging(hrBesluitBevestiging(item.boektInLoonadministratie));
        return;
      }
      if (item.soort === 'urenwijziging') {
        // `item.id` IS het correctie-id (zie `ApprovalQueueItem.id`).
        const res = await decideCorrection.mutateAsync({ id: item.id, decision: 'approve' });
        if (res.data.balanceWarning) {
          setBalanceWarning({
            applicantName: item.applicantName,
            hours: res.data.balanceWarning.hours,
          });
        }
        return;
      }
      const res = await decide.mutateAsync({ id: item.id, decision: 'approve' });
      if (res.data.balanceWarning) {
        setBalanceWarning({ applicantName: item.applicantName, hours: res.data.balanceWarning.hours });
      }
    } catch (err) {
      setDecideError(besluitFoutTekst(err, 'Goedkeuren mislukt. Probeer het opnieuw.'));
    }
  }

  /**
   * Story 7.6, AC-5: bij een race tussen lijst en besluit levert de server
   * `EMPLOYEE_UIT_DIENST` met de naam en de uitdienstdatum in `details`
   * (AD-9). Het scherm bouwt daar de VASTGESTELDE zin uit op — niet uit de
   * losse servertekst, die een andere formulering kan hebben. Zo krijgt de
   * beslisser dezelfde zin als in de lijst, zonder tweede opvraging.
   */
  function besluitFoutTekst(err: unknown, standaard: string): string {
    if (!(err instanceof ApiRequestError)) return standaard;
    if (err.code === 'EMPLOYEE_UIT_DIENST') {
      const details = err.details as
        | { naam?: string | null; uitDienstDatum?: string | null }
        | undefined;
      const zin = uitDienstZin(details?.naam ?? '', details?.uitDienstDatum ?? null);
      if (zin) return zin;
    }
    return err.message;
  }

  async function handleConfirmReject(reason: string) {
    if (!rejectTarget) return;
    setDecideError(null);
    setHrBevestiging(null);
    try {
      if (isHrBesluit(rejectTarget) && rejectTarget.soort === 'urenwijziging') {
        await decideHrStapCorrectie.mutateAsync({
          id: rejectTarget.id,
          decision: 'reject',
          reason,
        });
        setRejectTarget(null);
        return;
      }
      if (isHrBesluit(rejectTarget)) {
        // AC-7: de reden landt in `hr_decision_reason` — dus langs het
        // HR-pad, nooit langs het gewone besluitpad.
        await decideHrStap.mutateAsync({ id: rejectTarget.id, decision: 'reject', reason });
        setRejectTarget(null);
        return;
      }
      if (rejectTarget.soort === 'urenwijziging') {
        await decideCorrection.mutateAsync({ id: rejectTarget.id, decision: 'reject', reason });
      } else {
        await decide.mutateAsync({ id: rejectTarget.id, decision: 'reject', reason });
      }
      setRejectTarget(null);
    } catch (err) {
      setDecideError(besluitFoutTekst(err, 'Afwijzen mislukt. Probeer het opnieuw.'));
    }
  }

  /**
   * Story 7.7, AC-3: slaagt de terugname niet, dan is dat vrijwel altijd de
   * race — HR besliste net vóór de klik. De vastgestelde zin staat hier en
   * wordt niet ter plekke bedacht (KE-7).
   */
  async function handleConfirmRetract() {
    if (!retractTarget) return;
    setDecideError(null);
    setHrBevestiging(null);
    try {
      await retractApproval.mutateAsync({ id: retractTarget.id });
      setRetractTarget(null);
    } catch (err) {
      setDecideError(besluitFoutTekst(err, TERUGNEMEN_RACE));
    }
  }

  /** Story 7.8: de periode-aanpassing; de uren volgen uit het rooster. */
  async function handleConfirmEndDate(nieuweEinddatum: string) {
    if (!endDateTarget) return;
    setDecideError(null);
    setHrBevestiging(null);
    try {
      await updateEndDate.mutateAsync({ id: endDateTarget.id, endDate: nieuweEinddatum });
      setEndDateTarget(null);
    } catch (err) {
      setDecideError(
        besluitFoutTekst(err, 'De einddatum kon niet worden aangepast. Probeer het opnieuw.')
      );
    }
  }

  /**
   * Story 5.5: de soortcorrectie. De server herschrijft in dezelfde handeling
   * de boek-as en de HR-route; het scherm stuurt alleen de nieuwe soort en
   * leidt zelf niets af (KE-11).
   */
  async function handleConfirmSoort(nieuweSoort: Verlofsoort) {
    if (!soortTarget) return;
    setDecideError(null);
    setHrBevestiging(null);
    try {
      await corrigeerSoort.mutateAsync({ id: soortTarget.id, verlofsoort: nieuweSoort });
      setSoortTarget(null);
    } catch (err) {
      setDecideError(
        besluitFoutTekst(err, 'De soort kon niet worden aangepast. Probeer het opnieuw.')
      );
    }
  }

  return (
    <Layout>
      <div data-testid="approval-queue-page" className="max-w-5xl mx-auto">
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#1E293B]">Goedkeuringen</h1>
            <p className="mt-1 text-sm text-gray-500">Verlofaanvragen van je team, met beslis-context.</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Ingang alleen als er ook echt iemand te kiezen is. Laden en
                een mislukte lijst krijgen hun eigen zichtbare staat — stil
                verbergen is niet te onderscheiden van "geen rechten". */}
            {isLoadingSelectable ? (
              <span className="text-sm text-gray-400">Medewerkers laden…</span>
            ) : isSelectableError ? (
              <span data-testid="selectable-error" className="text-sm text-[#D64545]">
                Medewerkerslijst kon niet geladen worden
              </span>
            ) : selectableEmployees.length > 0 ? (
              <select
                data-testid="verlof-namens-select"
                aria-label="Verlof invoeren voor medewerker"
                value=""
                onChange={(e) => {
                  const chosen = selectableEmployees.find((emp) => emp.id === Number(e.target.value));
                  if (chosen) setOnBehalfFor({ id: chosen.id, name: chosen.name });
                }}
                className="px-3 py-1.5 text-sm border border-[#E2E8F0] rounded-lg text-[#1E293B]"
              >
                <option value="">Verlof invoeren voor…</option>
                {selectableEmployees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.name}
                    {emp.departmentName ? ` — ${emp.departmentName}` : ''}
                  </option>
                ))}
              </select>
            ) : null}
            <span
              data-testid="openstaand-teller"
              className="inline-block px-3 py-1 rounded-full bg-[#54949E]/10 text-[#54949E] text-sm font-medium"
            >
              {tellerTekst}
            </span>
          </div>
        </div>

        {onBehalfFor && (
          <LeaveRequestSheet
            isOpen
            forEmployee={onBehalfFor}
            onClose={() => setOnBehalfFor(null)}
            onSubmitted={() => setOnBehalfFor(null)}
          />
        )}

        {balanceWarning && (
          <div
            data-testid="balance-warning-banner"
            className="mb-4 p-3 rounded-md bg-[#D64545]/10 border border-[#D64545]/30 text-sm text-[#D64545] flex items-center justify-between"
          >
            <span>
              Let op: {balanceWarning.applicantName} heeft het verlofsaldo met {balanceWarning.hours} uur overschreden. De
              goedkeuring is doorgevoerd.
            </span>
            <button type="button" onClick={() => setBalanceWarning(null)} aria-label="Sluiten" className="ml-3 text-[#D64545]">
              ✕
            </button>
          </div>
        )}

        {hrBevestiging && (
          // AC-5/AC-13: de bevestiging staat als tekst, in de verlof-familie
          // (teal) en niet in het foutrood — er is niets misgegaan.
          <div
            data-testid="hr-besluit-bevestiging"
            role="status"
            className="mb-4 p-3 rounded-md bg-[#54949E]/10 border border-[#54949E]/30 text-sm text-[#2F5A7A] flex items-start justify-between"
          >
            <span>
              <span className="block">{hrBevestiging.regel}</span>
              {hrBevestiging.uitleg && (
                <span className="block mt-0.5 text-gray-600">{hrBevestiging.uitleg}</span>
              )}
            </span>
            <button
              type="button"
              onClick={() => setHrBevestiging(null)}
              aria-label="Sluiten"
              className="ml-3 text-[#2F5A7A]"
            >
              ✕
            </button>
          </div>
        )}

        {decideError && (
          <div data-testid="decide-error-banner" className="mb-4 p-3 rounded-md bg-[#D64545]/10 border border-[#D64545]/30 text-sm text-[#D64545]">
            {decideError}
          </div>
        )}

        <div className="mb-4">
          <label className="text-xs text-gray-500 mr-2" htmlFor="approval-status-filter">
            Status
          </label>
          <select
            id="approval-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as RequestStatus | 'alle')}
            className="text-sm border border-[#E2E8F0] rounded-md px-2 py-1"
          >
            {STATUS_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {isLoading ? (
          <div className="bg-white p-6 rounded-lg border border-[#E2E8F0] text-sm text-gray-500">Laden...</div>
        ) : error ? (
          <div className="bg-white p-6 rounded-lg border border-[#D64545]/30 text-sm text-[#D64545]">
            Je hebt geen toegang tot de goedkeuringslijst.
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="bg-white p-6 rounded-lg border border-[#E2E8F0] text-sm text-gray-500">
            Geen aanvragen gevonden.
          </div>
        ) : (
          <div className="space-y-3">
            {filteredItems.map((item) => (
              // De sleutel combineert soort en id: een aanvraag en een
              // urenwijziging kunnen hetzelfde nummer dragen (het zijn twee
              // tabellen).
              <div
                key={`${item.soort}-${item.id}`}
                data-testid="approval-queue-card"
                className="bg-white p-4 rounded-lg border border-[#E2E8F0]"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-[#1E293B]">{item.applicantName}</span>
                      {/* Story 11.1, AC-9: deze regel BLIJFT staan — hij
                          maakt de urenwijziging herkenbaar en zet niets uit. */}
                      {item.soort === 'urenwijziging' && <UrenwijzigingChip />}
                      {/* Story 7.3, AC-1: waaróm deze aanvraag bij HR ligt —
                          op de rij die er al staat, niet in een derde lijst.

                          Story 6.3, AC-1: de chip hangt aan de REDEN en niet
                          aan de vraag bij wie de aanvraag ligt. Een aanvraag
                          die over de maandruimte gaat is daarmee herkenbaar
                          zónder hem te openen, terwijl hij nog bij de
                          leidinggevende ligt — precies wat FR-6 vraagt.

                          De voorwaarde OP DE REDEN staat in de chip zelf: die
                          rendert niets zonder reden. Deze pagina leest die
                          reden nergens en combineert hem dus ook nergens met
                          een stand (N2 bij story 7.1).

                          De voorwaarde op de STAND staat wél hier, en kan
                          nergens anders staan (code review 2026-08-17). De
                          reden blijft na een besluit gewoon op de rij staan —
                          zij legt vast waaróm de aanvraag bij HR lág, en zegt
                          niets over de afloop. Zonder deze toets meldt een
                          afgewezen aanvraag nog steeds dat er iets bij HR
                          ligt. Tot story 6.3 verscheen de chip alleen op de
                          HR-stand (`ligtBijVan(item) === 'hr'`) en bleef dat
                          verborgen. */}
                      {item.requestStatus === 'in_afwachting' && <HrStapRedenChip rij={item} />}
                    </div>
                    <div className="text-sm text-gray-500">
                      {item.leaveTypeName} · {item.startDate} — {item.endDate} · {formatNlUren(item.hours)} uur
                    </div>
                    {/* Story 5.4, AC-7: dezelfde zin als de medewerker ziet,
                        uit dezelfde bron — bij een aanvraag van vóór de nieuwe
                        regels, en verder nergens. */}
                    <GeenSoortRegel rij={item} ingangsdatum={ingangsdatum} />
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {/* Story 7.2, AC-7: de chip vervangt de badge en staat er
                        niet naast — twee chips die allebei "er moet nog iets
                        gebeuren" zeggen, met verschillende woorden, is geen
                        extra informatie maar een raadsel. */}
                    {/* Story 8.2, AC-9: de stand komt van de server. Ontbreekt
                        hij, dan verschijnt de chip niet — er wordt niet
                        geraden. */}
                    {item.stand === 'wacht_op_hr' ? (
                      <WachtOpHrChip />
                    ) : (
                      <StatusBadge
                        status={item.requestStatus}
                        hours={item.hours}
                        displayStatus={item.displayStatus ?? null}
                      />
                    )}
                    {/* Story 3.3, UX V5: sync-chip los van de besluit-badge, alleen
                        relevant zodra er een besluit is (goedgekeurd). */}
                    {item.requestStatus === 'goedgekeurd' && (
                      <SyncStatusChip
                        syncStatus={item.syncStatus}
                        onRetry={() => retrySync.mutate(item.id)}
                        isRetrying={retrySync.isPending}
                      />
                    )}
                  </div>
                </div>

                {/* Story 2.6: een urenwijziging toont het correctieblok in
                    plaats van de saldo/overlap-context — daar gaat het besluit
                    niet over. */}
                {item.soort === 'urenwijziging' ? (
                  <CorrectieBlok item={item} />
                ) : (
                  <div className="mt-3 flex flex-wrap items-start justify-between gap-3">
                    <BalanceContext item={item} />
                    <OverlapIndicator item={item} />
                  </div>
                )}

                {item.decisionReason && (
                  <div className="mt-2 text-xs text-gray-500 border-t border-[#E2E8F0] pt-2">
                    Reden: {item.decisionReason}
                  </div>
                )}

                {/* Story 7.6, AC-6: de geblokkeerde aanvraag blijft zichtbaar
                    MÉT de reden — hij verdwijnt niet stil (FR-24). De zin komt
                    letterlijk uit §10.5 en staat er in beide lijsten, die van
                    de leidinggevende en die van HR. */}
                {item.uitDienstBlokkade &&
                  (() => {
                    const zin = uitDienstZin(
                      item.applicantName,
                      item.uitDienstBlokkade.uitDienstDatum
                    );
                    return zin ? (
                      <p
                        data-testid="uit-dienst-blokkade"
                        className="mt-2 text-xs text-[#D64545]"
                      >
                        {zin}
                      </p>
                    ) : null;
                  })()}

                {/* Story 6.3, AC-2: de vastgestelde zin uit §10.5, inline bij
                    het item — dezelfde vorm als de uit-dienst-regel hierboven.
                    Dit scherm heeft geen aparte detailweergave, dus "wat er bij
                    de aanvraag staat" staat hier. Hij rendert zichzelf weg
                    zodra er geen HR-stap of geen opgeslagen stand is.

                    De STANDtoets staat hier, net als bij de chip hierboven
                    (code review 2026-08-17): de zin eindigt op *"Keur je goed,
                    dan beoordeelt HR het daarna"* en dat is geen mededeling
                    voor een aanvraag die al is afgewezen. De vastgelegde stand
                    blijft na een besluit gewoon staan — hij zegt wat er bij het
                    INDIENEN gold en niets over de afloop. */}
                {item.requestStatus === 'in_afwachting' && <MaandruimteWaarschuwing item={item} />}

                {/* Story 11.1, AC-10: de vastgestelde regel die HR bij een
                    urenwijziging boven de maandruimte leest. Hij woont in
                    `HrStapStatus`, net als de rest van de HR-stap-teksten. */}
                <UrenwijzigingBovenMaandruimteRegel item={item} zietDeHrKant={zietDeHrKant} />

                {/* Story 11.1/11.2, code review 2026-08-18: dezelfde regel die
                    de medewerker in *Mijn verlof* leest, hier voor een
                    correctie die de leidinggevende of HR ZELF indiende (AD-24).
                    Zonder deze plek leest de indiener nooit wat er met zijn
                    eigen wijziging gebeurt — *Mijn verlof* toont alleen zijn
                    eigen verlof, en dit gaat over dat van iemand anders. De
                    reden en het besluitmoment op deze rij zijn die van de
                    CORRECTIE. */}
                <UrenwijzigingIndienerRegelOpDeLijst item={item} currentUserId={user?.id} />

                {/* Story 10.3/10.4: de gebeurtenis, het handboekrecht, de
                    afwijking, het verhuis-signaal en de markering — elk in de
                    kring waar hij hoort (§8.2, §8.3). De afwijking verdwijnt
                    zolang het aanpasvenster van story 7.8 openstaat: dáár staat
                    dezelfde zin, en twee keer dezelfde regel op één scherm is
                    geen extra informatie. */}
                <BijzonderVerlofContext
                  item={item}
                  zietDeHrKant={zietDeHrKant}
                  aanpasvensterOpen={
                    endDateTarget?.soort === item.soort && endDateTarget?.id === item.id
                  }
                />

                {/* Story 10.3, AC-5 (§10.5): wat de leidinggevende leest vóór
                    zijn goedkeuring. De reden woont in `HrStapStatus`, deze
                    pagina kent haar niet (N2, story 7.1). */}
                <LeidinggevendeDoorstuurRegel rij={item} zietDeHrKant={zietDeHrKant} />

                <div className="mt-3 flex justify-end">
                  <DecisionActions
                    item={item}
                    currentEmployeeId={user?.employeeId ?? undefined}
                    currentUserId={user?.id}
                    onApprove={handleApprove}
                    onReject={setRejectTarget}
                    onRetract={setRetractTarget}
                    onEditEndDate={setEndDateTarget}
                    onEditSoort={setSoortTarget}
                    magEinddatumAanpassen={zietDeHrKant}
                    magSoortAanpassen={zietDeHrKant}
                    zietDeHrKant={zietDeHrKant}
                    isPending={isPending}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Story 4.3: naslag, geen taak. Staat ónder de lijst en telt bewust
            niet mee in de openstaand-teller hierboven — het zijn geen
            openstaande beslissingen. Het blok haalt zijn eigen gegevens op en
            rendert zichzelf weg als er niets openstaat. */}
        <TeamOnbevestigdeUrenBlok />
      </div>

      <RejectDialog
        isOpen={rejectTarget !== null}
        onClose={() => setRejectTarget(null)}
        onConfirm={handleConfirmReject}
        isPending={isPending}
        // AC-7: bij een urenwijziging staat er letterlijk "De uren blijven op
        // 2 uur staan." — de huidige (oude) waarde, want afwijzen laat het
        // verlof precies zoals het was.
        blijftOpUren={
          rejectTarget?.soort === 'urenwijziging' ? rejectTarget.correctie?.oldHours : undefined
        }
        // Story 7.3, AC-7 (§10.6): wijst HR af, dan staat er bij het redenveld
        // wie die reden te zien krijgt. Bij elk ander besluit verandert er
        // niets aan deze dialoog.
        redenToelichting={
          rejectTarget && isHrBesluit(rejectTarget) ? REDEN_UITLEG_HR : undefined
        }
      />

      <RetractDialog
        isOpen={retractTarget !== null}
        onClose={() => setRetractTarget(null)}
        onConfirm={handleConfirmRetract}
        isPending={isPending}
      />

      <EndDateDialog
        item={endDateTarget}
        onClose={() => setEndDateTarget(null)}
        onConfirm={handleConfirmEndDate}
        isPending={isPending}
      />

      <SoortDialog
        item={soortTarget}
        onClose={() => setSoortTarget(null)}
        onConfirm={handleConfirmSoort}
        isPending={isPending}
      />
    </Layout>
  );
}
